import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import Head from 'next/head';
import { useRouter } from 'next/router';

import classNames from 'classnames';

import useStorageData from '@picsart/monetization/useStorageData';
import useCreditsInfo from '@picsart/monetization/useCreditsInfo';
import {
  NotifierDuration,
  ToastType,
  useNotifier,
} from '@picsart/design-system/components/Notifier';
import IS_BROWSER from '@picsart/rc/isBrowser';

import { sdkScriptDefaultSettings, pulseScriptAppName } from 'components/Layout/constants';
import JsonLD from 'components/JsonLD';

import { WEBSITE_SCHEMA_NOT_INCLUDED_PAGES } from 'tmp/main';
import { CHECKOUT_GTM_SOURCE } from 'store/constants';
import { useAppContext } from 'context/app';
import useAnalytics from 'hooks/useAnalytics';

import type { StyleType } from 'types/jss';

import organizationSchema from '../../../public/data/seo/organiztionSchema.json';
import websiteSchema from '../../../public/data/seo/websiteSchema.json';

import type { LayoutProps } from './types';

import useStyles from './styles';

const HeaderWithSkeleton = dynamic(() => import('@picsart/growth-rc/HeaderWithSkeleton'), {
  // header component cannot be rendered on server
  ssr: false,
});

const Footer = dynamic(() => import('components/Footer'));

const checkoutGtmData = { source: CHECKOUT_GTM_SOURCE };

const Layout = function ({
  ogImage = 'https://cdn140.picsart.com/60820868194827689611.jpg',
  pulseScriptSettings = sdkScriptDefaultSettings,
  twitterCard = 'summary_large_image',
  twitterSite = '@picsart',
  siteName = 'Picsart',
  ogDescription = '',
  description = '',
  ogTitle = '',
  canonicalUrl,
  alternateUrl,
  nextPageUrl,
  prevPageUrl,
  ogType = '',
  title = '',
  ogUrl = '',
  imageMetas,
  isWebView,
  children,
  nofollow,
  isMobile,
  blurred,
}: LayoutProps) {
  const { asPath } = useRouter();
  const { root, section, headerContainer } = useStyles({ blurred } as StyleType);
  const {
    notifierData: { description: notifierDescription, title: notifierTitle, status },
  } = useAppContext();
  const needWebsiteSchemaMarkUp = !WEBSITE_SCHEMA_NOT_INCLUDED_PAGES.includes(asPath.split('/')[1]);
  const { addNotification } = useNotifier();
  const storageData = useStorageData({ checkoutGtmData });
  const creditsData = useCreditsInfo({ checkoutGtmData });
  const { fireEvent } = useAnalytics();

  useEffect(() => {
    fireEvent('page_view', {
      platform: 'Website',
      page_category: 'Tech-seo',
      page_name: asPath,
    });
  }, [asPath, fireEvent]);

  useEffect(() => {
    if (status && title) {
      addNotification({
        duration: NotifierDuration.Notification,
        title: notifierTitle,
        description: notifierDescription,
        toastType: ToastType.Notification,
        status,
      });
    }
  }, [description, title]);

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="description" content={description} />
        <meta
          name="viewport"
          content="width=device-width, user-scalable=no, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0"
        />
        <meta name="device-type" content={isMobile ? 'mobile' : 'desktop'} />
        <meta httpEquiv="X-UA-Compatible" content="ie=edge" />
        <meta property="fb:app_id" content="108569252539536" />
        <meta property="og:type" content={ogType || 'picsartphotostudio:photo'} />
        <meta property="og:title" content={ogTitle || title} />
        <meta property="og:description" content={ogDescription || description} />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={ogUrl || canonicalUrl} />
        <meta name="twitter:card " content={twitterCard} />
        <meta name="twitter:site" content={twitterSite} />
        <meta name="twitter:description" content={ogDescription || 'Make Awesome Pictures'} />
        <meta name="medium" content="image" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="twitter:title" content={ogTitle || title || 'Picsart Photo Studio'} />
        <meta name="twitter:image" content={ogImage} />
        <meta
          name="twitter:description"
          content={ogDescription || description || 'Make Awesome Pictures'}
        />
        <meta name="robots" content="noindex, follow" />
        {nofollow && <meta name="robots" content="nofollow" />}
        <title>{title}</title>
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {nextPageUrl && <link rel="next" href={nextPageUrl} />}
        {prevPageUrl && <link rel="prev" href={prevPageUrl} />}
        {alternateUrl && (
          <>
            <link
              rel="alternate"
              href={`android-app://com.picsart.studio/https/picsart.com${alternateUrl}`}
            />
            <link rel="alternate" href={`ios-app://587366035/https/picsart.com${alternateUrl}`} />
          </>
        )}
        <meta
          name="google-site-verification"
          content="yFLvQHMKUSbDw5rOjIXaF4u_sNVNykinXHJFsOMDoNs"
        />
        <meta name="twitter:creator" content={imageMetas?.username || 'Picsart'} />
        {imageMetas && (
          <>
            <meta property="og:image:secure_url" content={ogImage} />
            <meta name="og:image:width" content={imageMetas.width} />
            <meta name="og:image:height" content={imageMetas.height} />
            <meta name="twitter:image:width" content={imageMetas.width} />
            <meta name="twitter:image:height" content={imageMetas.height} />
          </>
        )}
        {IS_BROWSER && (
          <Script
            src={`https://optifyr.com/pulse/${pulseScriptAppName}/pulse.js?include=${pulseScriptSettings.sdks.join(
              ',',
            )}${
              pulseScriptSettings.prefetch
                ? `&prefetch=${pulseScriptSettings.prefetch.join(',')}`
                : ''
            }`}
            async
            defer
          />
        )}
      </Head>
      <div className={root}>
        {!isWebView && (
          <div
            className={headerContainer}
            data-testid={isMobile ? 'mobile-header' : 'desktop-header'}
          >
            <HeaderWithSkeleton
              growthColors
              hideNavigationImages
              storageData={storageData}
              creditsData={creditsData}
              applicationName="marketplace"
              hideAppBanner={asPath.includes('templates')}
            />
          </div>
        )}
        <div className={classNames(section, 'main')}>{children}</div>
        {!isWebView && (
          <div className={classNames(section, 'footer')}>
            <Footer />
          </div>
        )}
        <JsonLD data={organizationSchema} />
        {needWebsiteSchemaMarkUp && <JsonLD data={websiteSchema} />}
      </div>
    </>
  );
};

export default Layout;
